// Audio Manager - Handles Web Audio API and HTML5 Audio fallback
export class AudioManager {
    constructor() {
        this.audioContext = null;
        this.masterGain = null;
        this.musicGain = null;
        this.sfxGain = null;
        
        this.sounds = new Map();
        this.music = null;
        this.musicSource = null;
        
        this.masterVolume = 1.0;
        this.musicVolume = 0.7;
        this.sfxVolume = 1.0;
        
        this.isMuted = false;
        this.isWebAudioSupported = false;
        
        this.init();
    }

    init() {
        this.checkWebAudioSupport();
        
        if (this.isWebAudioSupported) {
            this.initWebAudio();
        }
        
        console.log(`🎵 Audio Manager initialized (${this.isWebAudioSupported ? 'Web Audio API' : 'HTML5 Audio'})`);
    }

    checkWebAudioSupport() {
        this.isWebAudioSupported = !!(window.AudioContext || window.webkitAudioContext);
    }

    initWebAudio() {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            // Create gain nodes
            this.masterGain = this.audioContext.createGain();
            this.musicGain = this.audioContext.createGain();
            this.sfxGain = this.audioContext.createGain();
            
            // Connect gain nodes
            this.musicGain.connect(this.masterGain);
            this.sfxGain.connect(this.masterGain);
            this.masterGain.connect(this.audioContext.destination);
            
            // Set initial volumes
            this.updateVolumes();
            
        } catch (error) {
            console.warn('Web Audio API initialization failed:', error);
            this.isWebAudioSupported = false;
        }
    }

    async loadSound(key, src) {
        try {
            if (this.isWebAudioSupported) {
                await this.loadSoundWebAudio(key, src);
            } else {
                await this.loadSoundHTML5(key, src);
            }
        } catch (error) {
            console.error(`Failed to load sound ${key}:`, error);
            throw error;
        }
    }

    async loadSoundWebAudio(key, src) {
        const response = await fetch(src);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        
        this.sounds.set(key, {
            type: 'webaudio',
            buffer: audioBuffer,
            src: src
        });
    }

    async loadSoundHTML5(key, src) {
        return new Promise((resolve, reject) => {
            const audio = new Audio();
            
            audio.addEventListener('canplaythrough', () => {
                this.sounds.set(key, {
                    type: 'html5',
                    audio: audio,
                    src: src
                });
                resolve();
            });
            
            audio.addEventListener('error', (error) => {
                reject(new Error(`Failed to load audio: ${src}`));
            });
            
            audio.src = src;
            audio.load();
        });
    }

    playSound(key, volume = 1.0, loop = false) {
        try {
            const sound = this.sounds.get(key);
            if (!sound) {
                console.warn(`Sound not found: ${key}`);
                return null;
            }
            
            if (this.isWebAudioSupported) {
                return this.playSoundWebAudio(sound, volume, loop);
            } else {
                return this.playSoundHTML5(sound, volume, loop);
            }
        } catch (error) {
            console.error(`Failed to play sound ${key}:`, error);
            return null;
        }
    }

    playSoundWebAudio(sound, volume, loop) {
        if (!this.audioContext || !this.sfxGain) return null;
        
        const source = this.audioContext.createBufferSource();
        const gainNode = this.audioContext.createGain();
        
        source.buffer = sound.buffer;
        source.loop = loop;
        
        gainNode.gain.value = volume * this.sfxVolume * this.masterVolume;
        
        source.connect(gainNode);
        gainNode.connect(this.sfxGain);
        
        source.start();
        
        return {
            source: source,
            gainNode: gainNode,
            stop: () => source.stop()
        };
    }

    playSoundHTML5(sound, volume, loop) {
        const audio = sound.audio.cloneNode();
        
        audio.volume = volume * this.sfxVolume * this.masterVolume;
        audio.loop = loop;
        audio.play().catch(error => {
            console.warn('HTML5 Audio playback failed:', error);
        });
        
        return {
            audio: audio,
            stop: () => {
                audio.pause();
                audio.currentTime = 0;
            }
        };
    }

    playMusic(key, volume = 0.7) {
        // Stop current music
        this.stopMusic();
        
        const sound = this.sounds.get(key);
        if (!sound) {
            console.warn(`Music not found: ${key}`);
            return;
        }
        
        this.music = key;
        this.musicSource = this.playSound(key, volume, true);
    }

    stopMusic() {
        if (this.musicSource) {
            if (this.isWebAudioSupported) {
                this.musicSource.stop();
            } else {
                this.musicSource.stop();
            }
            this.musicSource = null;
            this.music = null;
        }
    }

    setMasterVolume(volume) {
        this.masterVolume = Math.max(0, Math.min(1, volume));
        this.updateVolumes();
    }

    setMusicVolume(volume) {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        this.updateVolumes();
    }

    setSFXVolume(volume) {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
        this.updateVolumes();
    }

    updateVolumes() {
        if (this.isWebAudioSupported) {
            if (this.masterGain) {
                this.masterGain.gain.value = this.isMuted ? 0 : this.masterVolume;
            }
            if (this.musicGain) {
                this.musicGain.gain.value = this.isMuted ? 0 : this.musicVolume;
            }
            if (this.sfxGain) {
                this.sfxGain.gain.value = this.isMuted ? 0 : this.sfxVolume;
            }
        }
    }

    mute() {
        this.isMuted = true;
        this.updateVolumes();
    }

    unmute() {
        this.isMuted = false;
        this.updateVolumes();
    }

    toggleMute() {
        this.isMuted = !this.isMuted;
        this.updateVolumes();
        return this.isMuted;
    }

    // Spatial audio support
    playSpatialSound(key, x, y, listenerX, listenerY, volume = 1.0) {
        if (!this.isWebAudioSupported) {
            return this.playSound(key, volume);
        }
        
        try {
            const sound = this.sounds.get(key);
            if (!sound || !this.audioContext) return null;
            
            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();
            const pannerNode = this.audioContext.createPanner();
            
            source.buffer = sound.buffer;
            
            // Calculate distance and panning
            const dx = x - listenerX;
            const dy = y - listenerY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const maxDistance = 500; // pixels
            
            // Set panner properties
            pannerNode.panningModel = 'HRTF';
            pannerNode.distanceModel = 'inverse';
            pannerNode.refDistance = 1;
            pannerNode.maxDistance = maxDistance;
            pannerNode.rolloffFactor = 1;
            pannerNode.coneInnerAngle = 360;
            pannerNode.coneOuterAngle = 0;
            pannerNode.coneOuterGain = 0;
            
            // Set position
            pannerNode.setPosition(x, y, 0);
            
            // Set volume based on distance
            const distanceFactor = Math.max(0, 1 - (distance / maxDistance));
            gainNode.gain.value = volume * this.sfxVolume * this.masterVolume * distanceFactor;
            
            source.connect(pannerNode);
            pannerNode.connect(gainNode);
            gainNode.connect(this.sfxGain);
            
            source.start();
            
            return {
                source: source,
                gainNode: gainNode,
                pannerNode: pannerNode,
                stop: () => source.stop()
            };
        } catch (error) {
            console.error('Spatial audio playback failed:', error);
            return this.playSound(key, volume);
        }
    }

    setListenerPosition(x, y, z = 0) {
        if (!this.isWebAudioSupported || !this.audioContext) return;
        
        if (this.audioContext.listener.setPosition) {
            this.audioContext.listener.setPosition(x, y, z);
        }
    }

    // Getters
    getMasterVolume() {
        return this.masterVolume;
    }

    getMusicVolume() {
        return this.musicVolume;
    }

    getSFXVolume() {
        return this.sfxVolume;
    }

    isMuted() {
        return this.isMuted;
    }

    hasWebAudioSupport() {
        return this.isWebAudioSupported;
    }

    // Resume audio context (required for some browsers)
    async resumeAudioContext() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            try {
                await this.audioContext.resume();
            } catch (error) {
                console.warn('Failed to resume audio context:', error);
            }
        }
    }
}